/**
 * `aiResponsesUtility.ts` — web port of Unity's `AIResponsesUtility.cs`: the
 * shared, non-realtime OpenAI Responses API client used by helper subagents
 * (Unity: `ActionClassifier`, Hierarchy/Knowledge context agents; web: the
 * knowledge transport's document search and the vector-store client).
 *
 * Contract parity with Unity:
 *  - POST `https://{aisvc_host}/v1/responses` with `Content-Type` / `Accept`
 *    JSON headers and the signed-in user's Cognito ID token. The Campfire
 *    proxy validates that token and replaces it with its server-side OpenAI
 *    credential before forwarding.
 *  - Response `output[]` walking for `message`/`output_text` items and
 *    `function_call` items, `usage` parsing (`input_tokens_details` /
 *    `output_tokens_details` — plural, unlike realtime events), and the
 *    nested-error message extraction.
 *
 * The browser calls `aisvc_host` directly. `oaiproxy` owns the restrictive
 * CORS allow-list and provider credential; `a1.campfire3d.com` is not in this
 * request path.
 */

import { getAiConfig } from "@ai/aiConfig";
import type { UsageDelta } from "@ai/observability/AISessionUsageTracker";
import { Ioc } from "@core/ioc/ioc";
import type { IAuthModel } from "@models/auth/IAuthModel";
import { AUTH_MODEL } from "@models/auth/IAuthModel";

export interface ResponsesFunctionCall {
  readonly callId: string;
  readonly name: string;
  readonly argumentsJson: string;
}

export class ResponsesRequestError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
  ) {
    super(message);
    this.name = "ResponsesRequestError";
  }
}

function aiServiceOrigin(): string {
  const host = getAiConfig().aiSvcHost.trim().replace(/\/+$/, "");
  if (/^https?:\/\//i.test(host)) return host;
  const protocol = /^(localhost|127\.0\.0\.1)(:\d+)?$/i.test(host) ? "http" : "https";
  return `${protocol}://${host}`;
}

/**
 * The same proxy addressed as a websocket origin. Kept beside
 * `aiServiceOrigin` so the two scheme rules cannot drift: `aisvc_host` may
 * already carry an http(s) scheme (a locally run oaiproxy is
 * `http://localhost:3000`), so translate it rather than prefixing `wss://`
 * onto a value that already has one. Mirrors Unity's
 * `AIResponsesUtility.GetWebSocketBaseUrl`.
 */
export function aiServiceWebSocketOrigin(): string {
  const origin = aiServiceOrigin();
  // `aiServiceOrigin` guarantees one of these two prefixes.
  return origin.startsWith("http://")
    ? `ws://${origin.slice("http://".length)}`
    : `wss://${origin.slice("https://".length)}`;
}

function resolveAiServiceUrl(path: string): string {
  return `${aiServiceOrigin()}/${path.replace(/^\/+/, "")}`;
}

async function fetchWithCampfireAuthorization(
  url: string,
  init: RequestInit,
): Promise<Response> {
  const auth = Ioc.resolve<IAuthModel>(AUTH_MODEL);
  const send = async (forceRefresh: boolean): Promise<Response> => {
    const idToken = await auth.getIdToken(forceRefresh);
    if (!idToken) {
      throw new ResponsesRequestError("responses_auth_unavailable", null);
    }
    const headers = new Headers(init.headers);
    headers.set("Authorization", `Bearer ${idToken}`);
    return await fetch(url, { ...init, headers });
  };

  const response = await send(false);
  return response.status === 401 ? await send(true) : response;
}

/**
 * Sends one request to an arbitrary AI-service path directly through the
 * Cognito-authenticated Campfire proxy and returns the raw `Response`.
 *
 * @param path AI-service path beginning with `/` (e.g. `/v1/vector_stores`).
 */
export async function aiProxyFetch(
  path: string,
  init: RequestInit,
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<Response> {
  const timeoutController = new AbortController();
  const timeoutHandle = setTimeout(() => timeoutController.abort(), timeoutMs);
  const onOuterAbort = (): void => timeoutController.abort();
  signal?.addEventListener("abort", onOuterAbort);

  try {
    return await fetchWithCampfireAuthorization(resolveAiServiceUrl(path), {
      ...init,
      signal: timeoutController.signal,
    });
  } catch (e) {
    if (signal?.aborted) throw e;
    const message = e instanceof Error ? e.message : String(e);
    throw new ResponsesRequestError(`request_failed - ${message}`, null);
  } finally {
    clearTimeout(timeoutHandle);
    signal?.removeEventListener("abort", onOuterAbort);
  }
}

export function resolveResponsesUrl(): string {
  return resolveAiServiceUrl("/v1/responses");
}

/**
 * Sends one Responses API request and returns the parsed JSON root.
 * Throws {@link ResponsesRequestError} on HTTP / network / parse failure and
 * rethrows aborts so callers can distinguish cancellation.
 */
export async function postResponsesAsync(
  body: Record<string, unknown>,
  signal: AbortSignal | undefined,
  timeoutMs: number,
): Promise<Record<string, unknown>> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  const timeoutController = new AbortController();
  const timeoutHandle = setTimeout(() => timeoutController.abort(), timeoutMs);
  const onOuterAbort = (): void => timeoutController.abort();
  signal?.addEventListener("abort", onOuterAbort);

  try {
    let response: Response;
    try {
      response = await fetchWithCampfireAuthorization(resolveResponsesUrl(), {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        signal: timeoutController.signal,
      });
    } catch (e) {
      if (signal?.aborted) throw e;
      const message = e instanceof Error ? e.message : String(e);
      throw new ResponsesRequestError(`request_failed - ${message}`, null);
    }

    const text = await response.text();
    if (!response.ok) {
      const detail = tryExtractOpenAIErrorMessage(text);
      throw new ResponsesRequestError(
        `request_failed (http ${response.status})${detail ? ` - ${detail}` : ""}`,
        response.status,
      );
    }

    try {
      return JSON.parse(text) as Record<string, unknown>;
    } catch {
      throw new ResponsesRequestError("response_parse_failed", response.status);
    }
  } finally {
    clearTimeout(timeoutHandle);
    signal?.removeEventListener("abort", onOuterAbort);
  }
}

/**
 * Streaming sibling of {@link postResponsesAsync} (`stream: true` + SSE).
 *
 * It exists for ONE reason: reasoning summaries have to reach the panel while
 * the model is still thinking. A buffered POST hands them over at the same
 * moment as the answer, which is exactly when they stop being interesting.
 *
 * The return value is the same parsed root the buffered call gives back — the
 * `response` object carried by the terminal `response.completed` event — so
 * every extractor below (`extractFunctionCalls`, `extractOutputItems`,
 * `tryParseResponsesUsage`, …) works unchanged on it.
 *
 * **It degrades to buffered parsing when the answer is not an event stream.**
 * A proxy that buffers (or a gateway that rejects the `stream` key) would
 * otherwise take the whole
 * bridge down rather than just costing it live reasoning. In that case the
 * body is parsed as one JSON root and any reasoning summary it carries is
 * replayed through `onReasoningDelta` before returning, so the panel still
 * shows what the model thought — just not as it happened.
 *
 * `timeoutMs` is an IDLE timeout once bytes start arriving, not a total: a
 * high-effort turn legitimately thinks for minutes, and a total budget would
 * cut it off mid-answer.
 */
export interface ResponsesStreamHandlers {
  /**
   * The response headers arrived. Fires before the status is checked, so a
   * refused request is timed too. `streamed` is false for a buffered answer,
   * whose reasoning and output arrive all at once at the end.
   */
  readonly onResponseHeaders?: (status: number, streamed: boolean) => void;
  /** `response.created`: the endpoint accepted the request and started on it. */
  readonly onResponseCreated?: () => void;
  /** An output item began: `"reasoning"`, `"function_call"`, `"message"`, … */
  readonly onOutputItemAdded?: (itemType: string) => void;
  /** A new reasoning summary part began (successive parts are separate paragraphs). */
  readonly onReasoningPartAdded?: () => void;
  /** Reasoning summary text, as it streams. */
  readonly onReasoningDelta?: (delta: string) => void;
  /** Assistant answer text, as it streams. */
  readonly onOutputTextDelta?: (delta: string) => void;
}

export async function streamResponsesAsync(
  body: Record<string, unknown>,
  signal: AbortSignal | undefined,
  timeoutMs: number,
  handlers: ResponsesStreamHandlers = {},
): Promise<Record<string, unknown>> {
  const timeoutController = new AbortController();
  let timeoutHandle = setTimeout(() => timeoutController.abort(), timeoutMs);
  const resetIdleTimeout = (): void => {
    clearTimeout(timeoutHandle);
    timeoutHandle = setTimeout(() => timeoutController.abort(), timeoutMs);
  };
  const onOuterAbort = (): void => timeoutController.abort();
  signal?.addEventListener("abort", onOuterAbort);

  try {
    let response: Response;
    try {
      response = await fetchWithCampfireAuthorization(resolveResponsesUrl(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify({ ...body, stream: true }),
        signal: timeoutController.signal,
      });
    } catch (e) {
      if (signal?.aborted) throw e;
      const message = e instanceof Error ? e.message : String(e);
      throw new ResponsesRequestError(`request_failed - ${message}`, null);
    }

    const contentType = (response.headers.get("content-type") ?? "").toLowerCase();
    const streamed = contentType.includes("text/event-stream") && response.body !== null;
    handlers.onResponseHeaders?.(response.status, streamed);

    if (!response.ok) {
      const detail = tryExtractOpenAIErrorMessage(await response.text().catch(() => ""));
      throw new ResponsesRequestError(
        `request_failed (http ${response.status})${detail ? ` - ${detail}` : ""}`,
        response.status,
      );
    }

    if (!streamed || !response.body) {
      // Buffered answer (proxy collapsed the stream, or the host answered JSON).
      const text = await response.text();
      let root: Record<string, unknown>;
      try {
        root = JSON.parse(text) as Record<string, unknown>;
      } catch {
        throw new ResponsesRequestError("response_parse_failed", response.status);
      }
      replayBufferedReasoning(root, handlers);
      return root;
    }

    return await consumeResponsesStream(response.body, handlers, resetIdleTimeout);
  } finally {
    clearTimeout(timeoutHandle);
    signal?.removeEventListener("abort", onOuterAbort);
  }
}

/**
 * Reads the SSE body and returns the terminal `response` object.
 *
 * Only the events the bridge acts on are decoded; the rest are ignored rather
 * than enumerated, so a new event type upstream is inert instead of fatal.
 */
async function consumeResponsesStream(
  body: ReadableStream<Uint8Array>,
  handlers: ResponsesStreamHandlers,
  onChunk: () => void,
): Promise<Record<string, unknown>> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  // A mutable holder rather than two `let`s: both are written from inside the
  // frame handler, and a captured `let` read back after the loop is exactly the
  // shape TypeScript's flow analysis has to give up on.
  const state: { root: Record<string, unknown> | null; failure: string | null } = {
    root: null,
    failure: null,
  };

  const handleEvent = (payload: string): void => {
    let event: unknown;
    try {
      event = JSON.parse(payload);
    } catch {
      return; // A non-JSON frame (e.g. the `[DONE]` sentinel) is not an error.
    }
    if (!isRecord(event)) return;
    const type = typeof event.type === "string" ? event.type : "";
    switch (type) {
      case "response.created":
        handlers.onResponseCreated?.();
        break;
      case "response.output_item.added":
        if (isRecord(event.item) && typeof event.item.type === "string") {
          handlers.onOutputItemAdded?.(event.item.type);
        }
        break;
      case "response.reasoning_summary_part.added":
        handlers.onReasoningPartAdded?.();
        break;
      case "response.reasoning_summary_text.delta":
        if (typeof event.delta === "string") handlers.onReasoningDelta?.(event.delta);
        break;
      case "response.output_text.delta":
        if (typeof event.delta === "string") handlers.onOutputTextDelta?.(event.delta);
        break;
      case "response.completed":
      case "response.incomplete":
        if (isRecord(event.response)) state.root = event.response;
        break;
      case "response.failed":
        if (isRecord(event.response)) state.root = event.response;
        state.failure =
          (isRecord(event.response) ? extractErrorMessage(event.response.error) : null) ??
          "response_failed";
        break;
      case "error":
        state.failure =
          extractErrorMessage(event.error) ?? extractErrorMessage(event) ?? "stream_error";
        break;
      default:
        break;
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      onChunk();
      buffer += decoder.decode(value, { stream: true });
      // SSE frames are separated by a blank line; tolerate CRLF. The match's
      // own length is what advances the buffer — a hand-counted 2 or 4 is how
      // a CRLF stream ends up one byte out of phase for the rest of the turn.
      const separator = /\r?\n\r?\n/;
      for (let match = separator.exec(buffer); match; match = separator.exec(buffer)) {
        const frame = buffer.slice(0, match.index);
        buffer = buffer.slice(match.index + match[0].length);
        // Per SSE, a frame's `data:` lines are one payload joined by newlines.
        // Handling them individually would break the moment a payload wraps.
        let data = "";
        for (const line of frame.split(/\r?\n/)) {
          if (!line.startsWith("data:")) continue;
          data += (data ? "\n" : "") + line.slice(5).replace(/^ /, "");
        }
        if (data.trim()) handleEvent(data);
      }
    }
  } finally {
    try {
      await reader.cancel();
    } catch {
      /* the stream is already finished or aborted */
    }
  }

  if (state.failure) throw new ResponsesRequestError(`request_failed - ${state.failure}`, null);
  if (!state.root) throw new ResponsesRequestError("response_stream_incomplete", null);
  return state.root;
}

/**
 * Replays the reasoning summary of a BUFFERED root through the delta handler,
 * so the non-streaming path still populates the panel's thinking block.
 */
function replayBufferedReasoning(
  root: Record<string, unknown>,
  handlers: ResponsesStreamHandlers,
): void {
  if (!handlers.onReasoningDelta) return;
  const summary = extractReasoningSummaryText(root);
  if (!summary) return;
  handlers.onReasoningPartAdded?.();
  handlers.onReasoningDelta(summary);
}

/**
 * The reasoning summary carried by a completed response: every `reasoning`
 * output item's `summary[].text`, joined into paragraphs. Empty string when the
 * model produced none (a non-reasoning model, or `summary` disabled).
 */
export function extractReasoningSummaryText(root: Record<string, unknown>): string {
  const output = root.output;
  if (!Array.isArray(output)) return "";
  const parts: string[] = [];
  for (const item of output) {
    if (!isRecord(item) || item.type !== "reasoning") continue;
    const summary = item.summary;
    if (!Array.isArray(summary)) continue;
    for (const entry of summary) {
      if (isRecord(entry) && typeof entry.text === "string" && entry.text.trim()) {
        parts.push(entry.text);
      }
    }
  }
  return parts.join("\n\n");
}

/** Port of `AIResponsesUtility.ExtractMessageOnlyText`. */
export function extractMessageOnlyText(root: Record<string, unknown>): string | null {
  const output = root.output;
  if (!Array.isArray(output)) return null;

  let combined = "";
  for (const item of output) {
    if (!isRecord(item) || item.type !== "message") continue;
    const content = item.content;
    if (!Array.isArray(content)) continue;
    for (const part of content) {
      if (isRecord(part) && part.type === "output_text" && typeof part.text === "string") {
        combined += part.text;
      }
    }
  }

  const trimmed = combined.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Port of `ActionClassifier.ExtractFunctionCalls`. */
export function extractFunctionCalls(root: Record<string, unknown>): ResponsesFunctionCall[] {
  const calls: ResponsesFunctionCall[] = [];
  const output = root.output;
  if (!Array.isArray(output)) return calls;

  for (const item of output) {
    if (!isRecord(item) || item.type !== "function_call") continue;
    const callId = typeof item.call_id === "string" ? item.call_id.trim() : "";
    const name = typeof item.name === "string" ? item.name.trim() : "";
    let argumentsJson = "{}";
    if (typeof item.arguments === "string") argumentsJson = item.arguments;
    else if (item.arguments != null) argumentsJson = JSON.stringify(item.arguments);
    if (callId && name) calls.push({ callId, name, argumentsJson });
  }

  return calls;
}

/**
 * Returns the raw response `output[]` items so a tool loop can echo them back
 * as the next request's input (Unity `AppendOutputItemsToInput`).
 */
export function extractOutputItems(root: Record<string, unknown>): unknown[] {
  const output = root.output;
  return Array.isArray(output) ? [...output] : [];
}

/** Port of `ResponsesApiUsage.TryParseFromResponse` (text-token subset). */
export function tryParseResponsesUsage(
  root: Record<string, unknown>,
  model: string,
): UsageDelta | null {
  const usage = root.usage;
  if (!isRecord(usage)) return null;

  const inputTokens = toCount(usage.input_tokens);
  const outputTokens = toCount(usage.output_tokens);
  if (inputTokens === 0 && outputTokens === 0) return null;

  let cachedInputTokens = 0;
  const inputDetails = usage.input_tokens_details;
  if (isRecord(inputDetails)) cachedInputTokens = toCount(inputDetails.cached_tokens);

  return { model, inputTokens, outputTokens, cachedInputTokens };
}

/** Port of `AIResponsesUtility.TryExtractOpenAIErrorMessage`. */
export function tryExtractOpenAIErrorMessage(bodyText: string | null | undefined): string | null {
  if (!bodyText || !bodyText.trim()) return null;
  try {
    const root = JSON.parse(bodyText) as unknown;
    if (!isRecord(root)) return null;
    const fromError = extractErrorMessage(root.error);
    if (fromError) return fromError;
    const response = root.response;
    if (isRecord(response)) return extractErrorMessage(response.error);
    return null;
  } catch {
    return null;
  }
}

function extractErrorMessage(error: unknown): string | null {
  if (typeof error === "string") return error.trim() || null;
  if (!isRecord(error)) return null;
  if (typeof error.message === "string" && error.message.trim()) return error.message.trim();
  const nested = error.error;
  if (isRecord(nested) && typeof nested.message === "string" && nested.message.trim()) {
    return nested.message.trim();
  }
  return null;
}

function toCount(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
